/**
 * What went wrong in an auth call to Clerk — as OUR vocabulary, never Clerk's
 * words. (Citizen first run · D10)
 *
 * Every sign-up, sign-in, password and email-code screen used to render
 * `errors[0].longMessage ?? errors[0].message` from Clerk: English-only, written
 * for developers, and sometimes about Clerk itself — "The monthly limit for
 * email messages in development (100) has been reached" reached a citizen's
 * sign-up form verbatim. The screens now show translated copy picked by the
 * KIND below, and a code this table does not know shows the screen's own
 * translated fallback. Clerk's text is never displayed.
 *
 * Pure and dependency-free, so web, mobile and business-web read one table.
 * The kind → copy key map lives in `@tonnie/locale` (`AUTH_ERROR_KEY`), and
 * `dynamic-key-coverage.spec.ts` ties it to `AUTH_ERROR_KINDS`.
 */

/** The auth failures a person can act on — each has its own copy. */
export const AUTH_ERROR_KINDS = [
  'email_taken',
  'account_not_found',
  'password_incorrect',
  'credentials_incorrect',
  'password_breached',
  'password_too_short',
  'password_too_weak',
  'password_too_long',
  'password_unchanged',
  'code_incorrect',
  'code_expired',
  'code_attempts_exhausted',
  'email_invalid',
  'email_not_allowed',
  'too_many_attempts',
  'account_locked',
  'already_signed_in',
  'bot_check_failed',
  'password_not_set',
  'reverification_required',
  'account_unavailable',
  'network_unreachable',
  /** The person said no at Google / Apple / Microsoft / LinkedIn. */
  'social_cancelled',
  /** That social account already belongs to a different Tonnie account. */
  'social_account_in_use',
] as const;

export type AuthErrorKind = (typeof AUTH_ERROR_KINDS)[number];

/** Clerk error code → kind. A code absent here is unknown, and says so. */
const KIND_BY_CODE: Readonly<Record<string, AuthErrorKind>> = {
  form_identifier_exists: 'email_taken',
  form_identifier_not_found: 'account_not_found',
  invitation_account_not_exists: 'account_not_found',
  form_password_incorrect: 'password_incorrect',
  // Clerk's own catalogues translate this as "Incorrect password" — it is what
  // an update with the wrong CURRENT password returns, not a strength failure.
  form_password_validation_failed: 'password_incorrect',
  form_password_digest_failed: 'password_incorrect',
  form_password_or_identifier_incorrect: 'credentials_incorrect',
  form_password_pwned: 'password_breached',
  form_password_compromised: 'password_breached',
  form_password_untrusted: 'password_breached',
  form_password_length_too_short: 'password_too_short',
  form_password_not_strong_enough: 'password_too_weak',
  form_password_size_in_bytes_exceeded: 'password_too_long',
  form_new_password_matches_current: 'password_unchanged',
  form_code_incorrect: 'code_incorrect',
  verification_expired: 'code_expired',
  verification_failed: 'code_attempts_exhausted',
  form_email_address_blocked: 'email_not_allowed',
  not_allowed_access: 'email_not_allowed',
  not_allowed_to_sign_up: 'email_not_allowed',
  sign_up_mode_restricted: 'email_not_allowed',
  sign_up_restricted_waitlist: 'email_not_allowed',
  too_many_requests: 'too_many_attempts',
  signup_rate_limit_exceeded: 'too_many_attempts',
  user_locked: 'account_locked',
  session_exists: 'already_signed_in',
  identifier_already_signed_in: 'already_signed_in',
  captcha_invalid: 'bot_check_failed',
  captcha_unavailable: 'bot_check_failed',
  captcha_missing_token: 'bot_check_failed',
  device_blocked: 'bot_check_failed',
  action_blocked: 'bot_check_failed',
  strategy_for_user_invalid: 'password_not_set',
  needs_reverification: 'reverification_required',
  session_reverification_needed: 'reverification_required',
  reverification_required: 'reverification_required',
  user_banned: 'account_unavailable',
  user_deactivated: 'account_unavailable',
  network_error: 'network_unreachable',
  oauth_access_denied: 'social_cancelled',
  external_account_exists: 'social_account_in_use',
};

/** Parameters whose "format/type invalid" means "that is not an email address". */
const EMAIL_PARAMS = new Set(['email_address', 'identifier']);
const INVALID_PARAM_CODES = new Set(['form_param_format_invalid', 'form_param_type_invalid']);

export interface ClassifiedClerkError {
  /** A kind with its own copy, or `null` — Clerk said something this table does not know. */
  kind: AuthErrorKind | null;
  /** Clerk's machine code, for analytics and reports; `'unknown'` when there is none. */
  code: string;
}

interface ClerkErrorEntry {
  code?: unknown;
  meta?: { paramName?: unknown };
}

function firstEntry(err: unknown): ClerkErrorEntry | null {
  if (!err || typeof err !== 'object') return null;
  const errors = (err as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0 && errors[0] && typeof errors[0] === 'object') {
    return errors[0] as ClerkErrorEntry;
  }
  // A Clerk RUNTIME error (the network, a misconfigured instance) carries its
  // code at the top level, with no `errors` array.
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? { code } : null;
}

/**
 * Read a Clerk error — an API response error (`errors[]`) or a runtime error
 * (`code`) — as a kind. Anything else (a plain `Error`, a string) is unknown.
 */
export function classifyClerkError(err: unknown): ClassifiedClerkError {
  const entry = firstEntry(err);
  const code = typeof entry?.code === 'string' ? entry.code : null;
  if (!code) return { kind: null, code: 'unknown' };

  if (INVALID_PARAM_CODES.has(code)) {
    const param = entry?.meta?.paramName;
    return {
      kind: typeof param === 'string' && EMAIL_PARAMS.has(param) ? 'email_invalid' : null,
      code,
    };
  }
  return { kind: KIND_BY_CODE[code] ?? null, code };
}

/** The kinds that mean the emailed CODE itself was refused. */
const REFUSED_CODE_KINDS: ReadonlySet<AuthErrorKind> = new Set([
  'code_incorrect',
  'code_expired',
  'code_attempts_exhausted',
]);

/**
 * Did Clerk refuse the CODE itself — wrong, expired, or out of attempts?
 *
 * A code screen empties its field on exactly this, so the next code is entered
 * whole: a full field keeps its caret on the last box, and a new code typed or
 * pasted over it replaced only that box (the right code after a wrong one went
 * out as `000004`). Any other failure — a refused new password, the network —
 * keeps the code, which may well be right.
 */
export function isRefusedCode(err: unknown): boolean {
  const { kind } = classifyClerkError(err);
  return kind !== null && REFUSED_CODE_KINDS.has(kind);
}
